import { useCallback, useEffect, useMemo, useState } from "react";
import {
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    deleteConfigurationsTests,
    getConfigurationsTestsByCompanyId,
    postConfigurationsTests,
    updateConfigurationsTests
} from "../../../../features/search/service/config-tests";
import { getMe } from "../../../../features/search/service/user-service";
import { getLanguagesSelect } from "../../../../features/search/service/languages-service";
import { User } from "../../../../shared/types/user";
import { SelectOption } from "../../../../shared/types/select-options";
import { MenuTop } from "../../../../components/menu/top/menu-top";
import {
    AdminCrudAlerts,
    AdminCrudDeleteDialog,
    AdminCrudFormDialog,
    AdminCrudHeader,
    AdminCrudSearch
} from "../../../../components/admin-crud/admin-crud";
import { useTranslation } from "../../../../shared/i18n/i18n-context";
import { ProjectLoader } from "../../../../components/project-loader/project-loader";
import "./styles/list.css";

interface ConfigTest {
    id: string | number;
    firstValue: string;
    secondValue: string;
    currency: string;
    languageId?: string;
    language_id?: string;
    companyId: string | number;
    language?: {
        id?: string;
        name: string;
        code: string;
    };
    createdAt?: string;
}

type ConfigFormState = {
    firstValue: string;
    secondValue: string;
    currency: string;
    languageId: string;
};

const emptyForm: ConfigFormState = {
    firstValue: "",
    secondValue: "",
    currency: "",
    languageId: ""
};

export const AdminListConfigs = () => {
    const { language, t } = useTranslation('admin');
    const { t: menuT } = useTranslation('menu');
    const [configs, setConfigs] = useState<ConfigTest[]>([]);
    const [me, setMe] = useState<User | null>(null);
    const [languages, setLanguages] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<ConfigTest | null>(null);
    const [form, setForm] = useState<ConfigFormState>(emptyForm);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState<ConfigTest | null>(null);

    const fetchConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const userData = await getMe();
            const companyId = userData.company?.id || userData.companyId;

            if (!companyId) {
                setConfigs([]);
                setMe(userData);
                setLanguages(await getLanguagesSelect());
                setError(t('userWithoutCompany'));
                return;
            }

            const [configsData, languagesData] = await Promise.all([
                getConfigurationsTestsByCompanyId(companyId),
                getLanguagesSelect()
            ]);

            setMe(userData);
            setConfigs(configsData || []);
            setLanguages(languagesData);
        } catch (err: any) {
            setError(err.message || t('configLoadError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    const filteredConfigs = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) {
            return configs;
        }

        return configs.filter((config) => {
            return [
                config.firstValue,
                config.secondValue,
                config.currency,
                config.language?.name,
                config.language?.code
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(term);
        });
    }, [configs, searchTerm]);

    const updateForm = (field: keyof ConfigFormState, value: string) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const openAddForm = () => {
        setEditingConfig(null);
        setForm({
            ...emptyForm,
            languageId: String(languages[0]?.id || "")
        });
        setFormOpen(true);
    };

    const openEditForm = (config: ConfigTest) => {
        setEditingConfig(config);
        setForm({
            firstValue: config.firstValue || "",
            secondValue: config.secondValue || "",
            currency: config.currency || "",
            languageId: String(config.language?.id || config.languageId || config.language_id || "")
        });
        setFormOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setFormOpen(false);
        setEditingConfig(null);
        setForm(emptyForm);
    };

    const handleSubmit = async () => {
        const companyId = me?.company?.id || me?.companyId;

        if (!form.firstValue || !form.secondValue || !form.currency || !form.languageId) {
            setError(t('fillAllFields'));
            return;
        }

        if (!companyId && !editingConfig) {
            setError(t('userWithoutCompany'));
            return;
        }

        try {
            setSaving(true);
            setError("");

            if (editingConfig?.id) {
                await updateConfigurationsTests({
                    id: String(editingConfig.id),
                    firstValue: form.firstValue,
                    secondValue: form.secondValue,
                    currency: form.currency,
                    languageId: form.languageId
                });
                setSuccessMessage(t('configUpdated'));
            } else {
                await postConfigurationsTests({
                    firstValue: form.firstValue,
                    secondValue: form.secondValue,
                    currency: form.currency,
                    companyId,
                    languageId: form.languageId
                });
                setSuccessMessage(t('configAdded'));
            }

            closeForm();
            await fetchConfigs();
        } catch (err: any) {
            setError(err.message || (editingConfig ? t('updateConfigError') : t('createConfigError')));
        } finally {
            setSaving(false);
        }
    };

    const openDeleteDialog = (config: ConfigTest) => {
        setConfigToDelete(config);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setConfigToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!configToDelete?.id) return;

        try {
            setDeleting(true);
            setError("");

            await deleteConfigurationsTests(String(configToDelete.id));
            setSuccessMessage(t('configRemoved'));
            closeDeleteDialog();
            await fetchConfigs();
        } catch (err: any) {
            setError(err.message || t('deleteConfigError'));
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (date?: string) => {
        if (!date) return t('notAvailable');
        return new Date(date).toLocaleDateString(language === 'en' ? 'en-US' : 'pt-BR');
    };

    return (
        <div className="admin-configs-page">
            <MenuTop />
            <div className="configs-container">
                <AdminCrudHeader
                    title={t('testConfigs')}
                    subtitle={`${configs.length} ${configs.length === 1 ? t('configSingular') : t('configPlural')} ${configs.length === 1 ? t('registeredSingular') : t('registeredPlural')}`}
                    addLabel={t('addConfig')}
                    addIcon={<AddCircleOutlineIcon />}
                    onAdd={openAddForm}
                    breadcrumbItems={[
                        { label: menuT('home'), to: '/home' },
                        { label: t('adminPanel'), to: '/admin-painel' },
                        { label: t('testConfigs') }
                    ]}
                />

                <AdminCrudSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={t('searchConfigs')}
                />

                <AdminCrudAlerts
                    error={error}
                    success={successMessage}
                    onClearError={() => setError("")}
                    onClearSuccess={() => setSuccessMessage("")}
                />

                {loading ? (
                    <div className="configs-loading">
                        <ProjectLoader label={t('loadingConfigs')} compact />
                    </div>
                ) : (
                    <TableContainer component={Paper} className="configs-table-container">
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('testConfigs')}</TableCell>
                                    <TableCell>{t('delayedValue')}</TableCell>
                                    <TableCell>{t('immediateValue')}</TableCell>
                                    <TableCell>{t('currency')}</TableCell>
                                    <TableCell>{t('language')}</TableCell>
                                    <TableCell>{t('createdAt')}</TableCell>
                                    <TableCell align="center">{t('actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredConfigs.map((config) => (
                                    <TableRow key={config.id} hover>
                                        <TableCell>
                                            <div className="configs-name-cell">
                                                <SettingsIcon />
                                                <strong>{t('configSingular')} #{config.id}</strong>
                                            </div>
                                        </TableCell>
                                        <TableCell>{config.currency} {config.firstValue}</TableCell>
                                        <TableCell>{config.currency} {config.secondValue}</TableCell>
                                        <TableCell>{config.currency}</TableCell>
                                        <TableCell>{config.language?.name || config.language?.code || t('notAvailable')}</TableCell>
                                        <TableCell>{formatDate(config.createdAt)}</TableCell>
                                        <TableCell align="center">
                                            <IconButton aria-label={t('editConfig')} onClick={() => openEditForm(config)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton aria-label={t('removeConfig')} color="error" onClick={() => openDeleteDialog(config)}>
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {filteredConfigs.length === 0 && (
                            <div className="configs-empty">{t('noConfigFound')}</div>
                        )}
                    </TableContainer>
                )}
            </div>

            <AdminCrudFormDialog
                open={formOpen}
                title={editingConfig ? t('editConfig') : t('addConfig')}
                closeLabel={t('close')}
                cancelLabel={t('cancel')}
                saveLabel={saving ? t('saving') : t('save')}
                saving={saving}
                onClose={closeForm}
                onSave={handleSubmit}
            >
                <TextField
                    label={t('delayedValue')}
                    value={form.firstValue}
                    onChange={(event) => updateForm("firstValue", event.target.value)}
                    fullWidth
                    required
                />
                <TextField
                    label={t('immediateValue')}
                    value={form.secondValue}
                    onChange={(event) => updateForm("secondValue", event.target.value)}
                    fullWidth
                    required
                />
                <TextField
                    label={t('currency')}
                    value={form.currency}
                    onChange={(event) => updateForm("currency", event.target.value)}
                    fullWidth
                    required
                />
                <FormControl fullWidth>
                    <InputLabel id="config-language-label">{t('language')}</InputLabel>
                    <Select
                        labelId="config-language-label"
                        label={t('language')}
                        value={form.languageId}
                        onChange={(event) => updateForm("languageId", event.target.value)}
                    >
                        {languages.map((language) => (
                            <MenuItem key={language.id} value={String(language.id)}>
                                {language.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </AdminCrudFormDialog>

            <AdminCrudDeleteDialog
                open={deleteDialogOpen}
                title={t('removeConfig')}
                message={t('confirmRemoveConfig').replace('{name}', `#${configToDelete?.id || ''}`)}
                cancelLabel={t('cancel')}
                confirmLabel={deleting ? t('removing') : t('remove')}
                deleting={deleting}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};
